import { getComponentFromName } from '@folklore/utils';
import classNames from 'classnames';
import { useState } from 'react';

import { useUnreadNotifications } from '../../hooks/useNotifications';

import * as NotificationsComponents from '../cards/notifications';
import Notification from './Notification';

import styles from '../../styles/popups/notifications-popups.module.css';

interface NotificationsPopupsProps {
    minimumDelayBetween: number;
}

const defaultMinimumDelayBetween = 30 * 1000;

function NotificationsPopups({
    minimumDelayBetween = defaultMinimumDelayBetween,
}: NotificationsPopupsProps) {
    const { items: notifications = [], markAsRead } = useUnreadNotifications();

    const [closedNotifications, setClosedNotifications] = useState([]);
    const [expanded, setExpanded] = useState(false);
    const onNotificationClosed = ({ id }: Notification) => {
        markAsRead(id);
    };

    const closeNotification = ({ id }: Notification) => {
        setClosedNotifications((current) => [...current, id]);
    };

    const activeNotifications = (notifications || [])
        .filter(({ id, type }) => {
            const NotificationComponent = getComponentFromName(NotificationsComponents, type);
            return closedNotifications.indexOf(id) === -1 && NotificationComponent !== null;
        })
        .map(({ id }) => id);
    const count = activeNotifications.length;
    return (notifications || []).map((notification) => {
        const { id, type } = notification || {};
        const NotificationComponent = getComponentFromName(NotificationsComponents, type);
        const index = activeNotifications.indexOf(id);
        const isActive = index === count - 1 || expanded;

        function requestClose() {
            if (isActive) {
                closeNotification(notification);
            }
        }

        function onClosed() {
            onNotificationClosed(notification);
        }

        function onClick(e) {
            if (!isActive) {
                e.preventDefault();
                setExpanded(true);
                return;
            }
            closeNotification(notification);
        }

        return NotificationComponent !== null ? (
            <Notification
                key={`notification-${id}`}
                id={`notification-${id}`}
                trackingId={`notification-${type}`}
                opened={closedNotifications.indexOf(id) === -1}
                requestClose={requestClose}
                onClosed={onClosed}
                withoutCloseButton={!isActive}
                withTransition
                className={classNames([
                    styles.notification,
                    {
                        [styles.expanded]: expanded,
                    },
                ])}
                style={{
                    '--notification-stack-index': Math.max(count - index - 1, 0),
                }}
            >
                <div className={styles.wrapper}>
                    <NotificationComponent
                        {...notification}
                        className={styles.card}
                        onClick={onClick}
                    />
                </div>
            </Notification>
        ) : null;
    });
}

export default NotificationsPopups;
